import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactLoading from "react-loading";
import styled from "styled-components";
import {
  analyzeNL2BLTL,
  fetchNL2BLTLExamples,
  translateNL2BLTL,
} from "./NL2BLTLUtility";

const EXAMPLE_INPUT =
  "Within 20 steps, FOXP3 eventually becomes active and remains active for 10 steps.";
const EXAMPLE_BLTL = "F[20](G[10](FOXP3 == 1))";

const MODEL_MODES = [
  {
    value: "hybrid",
    title: "Fine-tuned model + GPT review (Recommended)",
  },
  {
    value: "fine_tuned",
    title: "Fine-tuned model",
  },
  {
    value: "gpt",
    title: "GPT baseline",
  },
];

const Page = styled.div`
  min-height: calc(100vh - 124px);
  background: #f4f6fb;
  color: #1d2737;
  padding: 24px 24px 38px;

  @media (max-width: 700px) {
    min-height: calc(100vh - 104px);
    padding: 16px 12px 28px;
  }
`;

const Workspace = styled.div`
  width: min(1500px, 100%);
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Title = styled.h1`
  margin: 0;
  color: #00205b;
  font-size: 22px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: #627087;
  font-size: 13px;
`;

const SecondaryButton = styled.button`
  border: 1px solid #c8d2e2;
  border-radius: 7px;
  background: white;
  color: #00205b;
  font-weight: 800;
  font-size: 13px;
  padding: 9px 13px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const Panel = styled.section`
  background: white;
  border: 1px solid #d8e0ec;
  border-radius: 10px;
  box-shadow: 0 3px 14px rgba(0, 32, 91, 0.06);
  overflow: hidden;
  margin-top: 14px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 16px;
  border-bottom: 1px solid #e5eaf2;
  background: #fbfcff;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PanelTitle = styled.div`
  color: #00205b;
  font-weight: 800;
  font-size: 14px;
`;

const PanelNote = styled.div`
  color: #6a778b;
  font-size: 12px;
`;

const InputBody = styled.div`
  padding: 16px;
`;

const MainTextarea = styled.textarea`
  width: 100%;
  min-height: 126px;
  max-height: 300px;
  resize: vertical;
  border: 1px solid #c8d2e2;
  border-radius: 8px;
  padding: 15px 17px;
  box-sizing: border-box;
  color: #172033;
  font: 16px/1.55 "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  outline: none;

  &:focus {
    border-color: #003594;
    box-shadow: 0 0 0 3px rgba(0, 53, 148, 0.12);
  }
`;

const InputFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 12px;

  @media (max-width: 700px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 0 0 auto;

  > button {
    width: 190px;
    min-height: 44px;
  }

  @media (max-width: 700px) {
    align-items: stretch;
    flex-direction: column-reverse;

    > button {
      width: 100%;
    }
  }
`;

const PrimaryButton = styled.button`
  border: 1px solid #003594;
  border-radius: 7px;
  background: #003594;
  color: white;
  font-size: 14px;
  font-weight: 800;
  padding: 11px 18px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #002a77;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 18px 2px 0;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(props) => (props.$active || props.$complete ? "#003594" : "#8590a1")};
  font-size: 12px;
  font-weight: 800;

  &::before {
    content: "${(props) => (props.$complete ? "✓" : props.$number)}";
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    border-radius: 50%;
    color: ${(props) => (props.$active ? "white" : props.$complete ? "#003594" : "#718096")};
    background: ${(props) => (props.$active ? "#003594" : props.$complete ? "#e8f0ff" : "white")};
    border: 1px solid ${(props) => (props.$active || props.$complete ? "#003594" : "#cbd5e1")};
  }

  @media (max-width: 560px) {
    span {
      display: none;
    }
  }
`;

const AnalysisReview = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-left: 3px solid #1662bd;
  border-radius: 4px;
  background: #f5f8fd;
  color: #4d5b70;
  font-size: 13px;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #294e7c;
  }
`;

const AnalysisBody = styled.div`
  padding: 16px;
`;

const Editor = styled.div`
  min-width: 0;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #536176;
  font-size: 11px;
  font-weight: 800;
`;

const BehaviorTextarea = styled.textarea`
  width: 100%;
  min-height: 86px;
  resize: vertical;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 11px 12px;
  color: #1c2738;
  font: 14px/1.5 inherit;
  outline: none;

  &:focus {
    border-color: #1662bd;
    box-shadow: 0 0 0 3px rgba(22, 98, 189, 0.1);
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const TextInput = styled.input`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 0 11px;
  color: #1c2738;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #1662bd;
    box-shadow: 0 0 0 3px rgba(22, 98, 189, 0.1);
  }
`;

const MissingBox = styled.div`
  margin-top: 12px;
  padding: 11px 13px;
  border: 1px solid #efd59c;
  border-radius: 7px;
  background: #fffaee;
  color: #6f4c0b;
  font-size: 12px;

  ul {
    margin: 7px 0 0;
    padding-left: 18px;
  }
`;

const ConfirmRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 13px;
  color: #4f5d72;
  font-size: 12px;
  line-height: 1.45;

  input {
    margin-top: 2px;
  }
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 11px;
  padding: 16px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ModeOption = styled.label`
  display: flex;
  align-items: center;
  min-height: 64px;
  border: 1px solid ${(props) => (props.$selected ? "#1662bd" : "#d4deeb")};
  border-radius: 9px;
  background: ${(props) => (props.$selected ? "#f4f8ff" : "white")};
  padding: 16px;
  cursor: pointer;

  input {
    margin-right: 8px;
  }
`;

const ModeTitle = styled.span`
  color: #18375f;
  font-size: 13px;
  font-weight: 800;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 16px 16px;

  > button {
    min-width: 190px;
    min-height: 44px;
  }

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column-reverse;
  }
`;

const LoadingBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 110px;
  color: #526177;
  font-size: 13px;
`;

const ErrorBox = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #efc1bc;
  border-radius: 8px;
  background: #fff3f1;
  color: #922b21;
  font-size: 13px;
`;

const ResultBody = styled.div`
  padding: 16px;
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 14px;
  margin-top: 14px;

  ${Panel} {
    margin-top: 0;
  }

  @media (max-width: 950px) {
    grid-template-columns: 1fr;
  }
`;

const ResultActions = styled(ActionBar)`
  padding: 14px 0 0;
`;

const Formula = styled.textarea`
  width: 100%;
  min-height: 88px;
  resize: vertical;
  box-sizing: border-box;
  border: 0;
  border-radius: 7px;
  background: #f7f9fc;
  padding: 14px;
  color: #101827;
  font: 17px/1.55 "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  outline: none;
`;

const CopyRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const ReviewBox = styled.div`
  margin-top: 12px;
  padding: 11px 13px;
  border-left: 3px solid #1662bd;
  background: #f5f8fd;
  color: #4d5b70;
  font-size: 12px;
  line-height: 1.45;
`;

const PreviewBody = styled.div`
  min-height: 230px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  box-sizing: border-box;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: contain;
`;

const PreviewEmpty = styled.div`
  color: #667386;
  font-size: 13px;
  text-align: center;
`;

function listText(values) {
  return (values || []).join(", ");
}

function parseVariables(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseTimeBounds(value) {
  return value
    .split(/[\s,]+/)
    .map((item) => Number.parseInt(item, 10))
    .filter((item) => Number.isFinite(item));
}

export default function NL2BLTLApp() {
  const [currentStep, setCurrentStep] = useState(1);
  const [inputText, setInputText] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [behaviorText, setBehaviorText] = useState("");
  const [variablesText, setVariablesText] = useState("");
  const [timeBoundsText, setTimeBoundsText] = useState("");
  const [preparationMethod, setPreparationMethod] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [mode, setMode] = useState("hybrid");
  const [result, setResult] = useState(null);
  const [bltl, setBltl] = useState("");
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [examples, setExamples] = useState([]);
  const [exampleIndex, setExampleIndex] = useState(0);
  const textareaRef = useRef(null);

  const parsedVariables = useMemo(() => parseVariables(variablesText), [variablesText]);
  const parsedTimeBounds = useMemo(() => parseTimeBounds(timeBoundsText), [timeBoundsText]);
  const hasTimeBounds = parsedTimeBounds.length > 0;
  const canTranslate = Boolean(analysis && behaviorText.trim() && hasTimeBounds && confirmed);

  useEffect(() => {
    let mounted = true;
    fetchNL2BLTLExamples().then((items) => {
      if (mounted) setExamples(items);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const applyCandidate = useCallback((candidate) => {
    setBehaviorText(candidate.normalized_text || "");
    setVariablesText(listText(candidate.variables));
    setTimeBoundsText(listText(candidate.time_bounds));
    setConfirmed(false);
    setResult(null);
    setBltl("");
    setPreview(null);
  }, []);

  const handleAnalyze = useCallback(async () => {
    const text = inputText.trim();
    if (!text || analyzing) return;
    setAnalyzing(true);
    setError("");
    setAnalysis(null);
    setPreparationMethod(null);
    setResult(null);
    setBltl("");
    setPreview(null);
    try {
      const data = await analyzeNL2BLTL(text);
      setAnalysis(data);
      setPreparationMethod("gpt");
      const first = data.candidates?.[0];
      if (first) applyCandidate(first);
      setCurrentStep(2);
    } catch (err) {
      setError(err.message || "Input analysis failed.");
    } finally {
      setAnalyzing(false);
    }
  }, [analyzing, applyCandidate, inputText]);

  const handleUseDirectly = useCallback(() => {
    const text = inputText.trim();
    if (!text || analyzing || translating) return;
    const candidate = {
      id: "direct_input",
      source_excerpt: text,
      normalized_text: text,
      variables: [],
      time_bounds: [],
      missing_information: [],
      ready: true,
    };
    setAnalysis({
      input_quality: "ready",
      summary: "",
      candidates: [candidate],
    });
    setPreparationMethod("direct");
    setError("");
    applyCandidate(candidate);
    setCurrentStep(2);
  }, [analyzing, applyCandidate, inputText, translating]);

  const handleTranslate = useCallback(async () => {
    if (!canTranslate || translating) return;
    setTranslating(true);
    setError("");
    setCopied(false);
    setPreview(null);
    try {
      const data = await translateNL2BLTL({
        text: inputText.trim(),
        mode,
        behaviorText: behaviorText.trim(),
        variables: parsedVariables,
        timeBounds: parsedTimeBounds,
      });
      if (data.error) {
        setError(data.error);
        setResult(null);
        return;
      }
      setResult(data);
      setBltl(data.bltl || "");
      setPreview(data.preview || null);
    } finally {
      setTranslating(false);
    }
  }, [
    behaviorText,
    canTranslate,
    inputText,
    mode,
    parsedTimeBounds,
    parsedVariables,
    translating,
  ]);

  const handleUseExample = useCallback(() => {
    const example = examples[exampleIndex] || { input: EXAMPLE_INPUT };
    setInputText(example.input || EXAMPLE_INPUT);
    setCurrentStep(1);
    setAnalysis(null);
    setBehaviorText("");
    setVariablesText("");
    setTimeBoundsText("");
    setPreparationMethod(null);
    setConfirmed(false);
    setResult(null);
    setBltl("");
    setPreview(null);
    setError("");
    setExampleIndex((exampleIndex + 1) % Math.max(examples.length, 1));
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  }, [exampleIndex, examples]);

  const handleCopy = useCallback(async () => {
    if (!bltl.trim()) return;
    await navigator.clipboard.writeText(bltl.trim());
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }, [bltl]);

  const goToInput = useCallback(() => {
    setCurrentStep(1);
    setError("");
  }, []);

  const goToConfirmation = useCallback(() => {
    setCurrentStep(2);
    setResult(null);
    setBltl("");
    setPreview(null);
    setError("");
  }, []);

  const goToGeneration = useCallback(() => {
    if (!canTranslate) return;
    setCurrentStep(3);
    setResult(null);
    setBltl("");
    setPreview(null);
    setError("");
  }, [canTranslate]);

  const showModeSelection = useCallback(() => {
    setResult(null);
    setBltl("");
    setPreview(null);
    setError("");
  }, []);

  return (
    <Page>
      <Workspace>
        <Header>
          <div>
            <Title>NL2BLTL</Title>
            <Subtitle>
            Automated Generation of Bounded Linear Temporal Logic from Natural Language for Systems Biology.
            </Subtitle>
          </div>
          <SecondaryButton onClick={handleUseExample}>Use Example</SecondaryButton>
        </Header>

        <Steps>
          <Step $number="1" $active={currentStep === 1} $complete={currentStep > 1}>
            <span>Input</span>
          </Step>
          <Step $number="2" $active={currentStep === 2} $complete={currentStep > 2}>
            <span>Review input</span>
          </Step>
          <Step $number="3" $active={currentStep === 3}>
            <span>Generate BLTL</span>
          </Step>
        </Steps>

        {currentStep === 1 && (
          <Panel>
            <PanelHeader>
              <PanelTitle>1. Enter natural language or biomedical text</PanelTitle>
            </PanelHeader>
            <InputBody>
              <MainTextarea
                ref={textareaRef}
                value={inputText}
                placeholder={EXAMPLE_INPUT}
                disabled={analyzing || translating}
                onChange={(event) => {
                  setInputText(event.target.value);
                  setAnalysis(null);
                  setPreparationMethod(null);
                  setConfirmed(false);
                  setResult(null);
                  setBltl("");
                  setPreview(null);
                }}
              />
              <InputFooter>
                <InputActions>
                  <SecondaryButton
                    type="button"
                    onClick={handleUseDirectly}
                    disabled={!inputText.trim() || analyzing || translating}
                  >
                    Use text directly
                  </SecondaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={handleAnalyze}
                    disabled={!inputText.trim() || analyzing || translating}
                  >
                    {analyzing ? "Analyzing…" : "Analyze →"}
                  </PrimaryButton>
                </InputActions>
              </InputFooter>
            </InputBody>
          </Panel>
        )}

        {currentStep === 2 && analysis && (
          <Panel>
            <PanelHeader>
              <PanelTitle>2. Review input</PanelTitle>
            </PanelHeader>
            {analysis.candidates?.length > 0 ? (
              <AnalysisBody>
                <Editor>
                  {preparationMethod !== "direct" && (
                    <AnalysisReview>
                      <strong>GPT review</strong>
                      {analysis.summary || "Review the extracted input and complete missing details."}
                    </AnalysisReview>
                  )}
                  <FieldLabel htmlFor="nl2bltl-behavior">
                    {preparationMethod === "direct" ? "Input" : "Rephrased input"}
                  </FieldLabel>
                  <BehaviorTextarea
                    id="nl2bltl-behavior"
                    value={behaviorText}
                    onChange={(event) => {
                      setBehaviorText(event.target.value);
                      setConfirmed(false);
                    }}
                  />
                  <FieldGrid>
                    <div>
                      <FieldLabel htmlFor="nl2bltl-variables">Variables</FieldLabel>
                      <TextInput
                        id="nl2bltl-variables"
                        value={variablesText}
                        onChange={(event) => {
                          setVariablesText(event.target.value);
                          setConfirmed(false);
                        }}
                      />
                    </div>
                    <div>
                      <FieldLabel htmlFor="nl2bltl-bounds">Bounds</FieldLabel>
                      <TextInput
                        id="nl2bltl-bounds"
                        value={timeBoundsText}
                        required
                        onChange={(event) => {
                          setTimeBoundsText(event.target.value);
                          setConfirmed(false);
                        }}
                      />
                    </div>
                  </FieldGrid>

                  <ConfirmRow>
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(event) => setConfirmed(event.target.checked)}
                    />
                    <span>
                      {preparationMethod === "direct"
                        ? "The input is correct."
                        : "The rephrased input is correct."}
                    </span>
                  </ConfirmRow>
                </Editor>
              </AnalysisBody>
            ) : (
              <InputBody>
                <ErrorBox>
                  No bounded biological behavior was found. Revise the input to identify an entity,
                  its state or threshold, and when the behavior should occur.
                </ErrorBox>
              </InputBody>
            )}
            <ActionBar>
              <SecondaryButton type="button" onClick={goToInput}>
                ← Back
              </SecondaryButton>
              {analysis.candidates?.length > 0 && (
                <PrimaryButton type="button" onClick={goToGeneration} disabled={!canTranslate}>
                  Next: Choose model ≈
                </PrimaryButton>
              )}
            </ActionBar>
          </Panel>
        )}

        {currentStep === 3 && !translating && !result && (
          <Panel>
            <PanelHeader>
              <PanelTitle>3. Choose a translation mode</PanelTitle>
            </PanelHeader>
            <ModeGrid>
              {MODEL_MODES.map((option) => (
                <ModeOption key={option.value} $selected={mode === option.value}>
                  <input
                    type="radio"
                    name="nl2bltl-model-mode"
                    value={option.value}
                    checked={mode === option.value}
                    onChange={(event) => setMode(event.target.value)}
                  />
                  <ModeTitle>{option.title}</ModeTitle>
                </ModeOption>
              ))}
            </ModeGrid>
            <ActionBar>
              <SecondaryButton type="button" onClick={goToConfirmation}>
                ← Back
              </SecondaryButton>
              <PrimaryButton type="button" onClick={handleTranslate} disabled={!canTranslate}>
                Generate BLTL →
              </PrimaryButton>
            </ActionBar>
          </Panel>
        )}

        {currentStep === 3 && translating && (
          <Panel>
            <LoadingBox>
              <ReactLoading type="balls" color="#003594" width={38} height={38} />
              {mode === "hybrid"
                ? "Generating a fine-tuned draft and reviewing it…"
                : "Generating BLTL…"}
            </LoadingBox>
          </Panel>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        {currentStep === 3 && !translating && result && (
          <>
            <ResultGrid>
            <Panel>
              <PanelHeader>
                <PanelTitle>Generated BLTL</PanelTitle>
              </PanelHeader>
              <ResultBody>
                <Formula
                  value={bltl}
                  placeholder={EXAMPLE_BLTL}
                  onChange={(event) => {
                    setBltl(event.target.value);
                    setPreview(null);
                  }}
                />
                {result.fallback_reason && (
                  <ReviewBox>
                    <strong>Endpoint fallback:</strong> {result.fallback_reason}
                  </ReviewBox>
                )}
                {result.review && (
                  <ReviewBox>
                    <strong>GPT review:</strong> {result.review.explanation}
                    {result.review.clarification_question && (
                      <div>Clarification: {result.review.clarification_question}</div>
                    )}
                  </ReviewBox>
                )}
                {result.draft_bltl && result.draft_bltl !== result.bltl && (
                  <ReviewBox>
                    <strong>Fine-tuned draft:</strong> {result.draft_bltl}
                  </ReviewBox>
                )}
                {result.validation?.issues?.length > 0 && (
                  <MissingBox>
                    <strong>Basic syntax warnings:</strong>
                    <ul>
                      {result.validation.issues.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </MissingBox>
                )}
                <CopyRow>
                  <SecondaryButton onClick={handleCopy} disabled={!bltl.trim()}>
                    {copied ? "Copied" : "Copy formula"}
                  </SecondaryButton>
                </CopyRow>
              </ResultBody>
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Specification Preview</PanelTitle>
                <PanelNote>Illustrative behavior—not a verification result or unique trajectory</PanelNote>
              </PanelHeader>
              <PreviewBody>
                {preview?.available && preview.image ? (
                  <PreviewImage src={preview.image} alt="BLTL specification preview trajectory" />
                ) : (
                  <PreviewEmpty>
                    {preview?.error || "No preview is available for this BLTL pattern."}
                  </PreviewEmpty>
                )}
              </PreviewBody>
            </Panel>
            </ResultGrid>
            <ResultActions>
              <SecondaryButton type="button" onClick={showModeSelection}>
                ← Back to models
              </SecondaryButton>
              <PrimaryButton type="button" onClick={goToInput}>
                Edit input →
              </PrimaryButton>
            </ResultActions>
          </>
        )}
      </Workspace>
    </Page>
  );
}
